<template>
  <div class="d-flex flex-column" ref="containerRef">
    <section class="w-100 top-section pt-5">
      <CloseButton @on-click="close" class="text-black mt-3" />
      <div class="d-flex flex-column align-items-center mx-auto w-100 px-3">
        <img :src="useAsset('img/credit-line/coins-sparkle.svg')" alt="coins" />
        <h1 class="text-center">{{ t("CREDIT_LIMIT_INCREASE.TITLE") }}</h1>
        <h2 class="text-center px-4">
          {{ t("CREDIT_LIMIT_INCREASE.DESCRIPTION") }}
        </h2>

        <div class="text-center w-100">
          <span class="text-center new-amount">{{ newLimitAmount }}</span>
          <p class="text-center old-amount">
            {{ t("CREDIT_LIMIT_INCREASE.PREVIOUS_LIMIT", { previousLimit }) }}
          </p>
        </div>
      </div>
    </section>

    <section class="mt-5 w-100 px-4 d-flex justify-content-start flex-grow-1">
      <BulletList :items="LIST_ITEMS" gap="26px" class="w-100" />
    </section>

    <section
      class="w-100 px-4 d-flex justify-content-center flex-column pb-3 mb-0 mt-auto position-relative bottom-0"
    >
      <Button class="mb-2" type="primary" @click="goToWithdrawal">
        {{ t("CREDIT_LIMIT_INCREASE.BUTTON_USE_NEW_LIMIT") }}
      </Button>
      <Button type="link" @click="goToHelp">{{
        t("CREDIT_LIMIT_INCREASE.LINK_REVERSE_LIMIT")
      }}</Button>
    </section>
  </div>
</template>

<script setup lang="ts">
import useAsset from "@/apps/credit-line/helpers/useAsset";
import CloseButton from "@/shared-components/consumer/CloseButton.vue";
import { onMounted, ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import BulletList from "@/apps/credit-line/components/BulletList.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import { resizeComponentHelper } from "@/apps/credit-line/helpers/resizeComponent.helper";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import { useRouter } from "vue-router";
import { Service as ApiService } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";

const { t } = useI18n();
const router = useRouter();
const apiService = new ApiService();
const containerRef = ref<HTMLElement | null>(null);
const newLimitAmount = ref<string>("");
const previousLimit = ref<string>("");

const close = (): void => {
  safeAndroidCall("dismissWebView");
};

const LIST_ITEMS = [
  {
    text: t("CREDIT_LIMIT_INCREASE.BENEFITS.NO_FEES"),
    icon: "img/icons/checked_circle.svg",
  },
  {
    text: t("CREDIT_LIMIT_INCREASE.BENEFITS.SAME_LINE"),
    icon: "img/icons/checked_circle.svg",
  },
  {
    text: t("CREDIT_LIMIT_INCREASE.BENEFITS.ONLY_PAY_WHAT_YOU_USE"),
    icon: "img/icons/checked_circle.svg",
  },
];

const goToWithdrawal = (): void => {
  router.replace({ name: "CreditLineWithdrawView", query: { reload: "true" } });
};

const goToHelp = (): void => {
  router.replace({ name: "CreditLineHelpView" });
};

const fetchAcceptNewLimit = async () => {
  try {
    const res = await apiService.increaseCreditLimit({
      deviceTag: AuthenticationHelper.getDeviceTag(),
    });

    const data = res.data?.data;

    newLimitAmount.value = currencyFormat(data?.limitAmount);
    previousLimit.value = currencyFormat(data?.oldLimitAmount);

    safeAndroidCall("finishedLoading");
  } catch (error) {
    ErrorHandler.handleError(error as Error);
  }
};

onMounted(async () => {
  resizeComponentHelper(containerRef);

  void fetchAcceptNewLimit();
});
</script>

<style lang="scss" scoped>
.top-section {
  background: #f2f2f2;
}

h1 {
  color: var(--Typography-Text-2-ICON, #595a5a);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
}

h2 {
  color: var(--Typography-Text-3-sub, #8b8b8b);
  font-feature-settings: "liga" off, "clig" off;

  font-family: Inter, serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

.new-amount {
  color: var(--Primary-Main-green, #007c3b);
  text-align: center;
  font-family: Inter, serif;
  font-size: 40px;
  font-style: normal;
  font-weight: 600;
  line-height: 48px; /* 120% */
  letter-spacing: 0.18px;
}

.old-amount {
  color: var(--Typography-Text-3-sub, #8b8b8b);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;

  font-family: Inter, serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}
</style>
